<script setup lang="ts">
import { computed, ref } from 'vue';

import { toArrayClass } from 'lib/components/helpers';

import { InlineMessageEmits, InlineMessageProps } from './InlineMessage.vue.d';
import Button from '../button/Button.vue';
import Icon from '../icon/Icon.vue';
import { WangsIcons } from '../icon/Icon.vue.d';

const props = withDefaults(defineProps<InlineMessageProps>(), {
  severity: 'secondary',
  removable: true,
  showIcon: true,
  loading: false,
});

defineEmits<InlineMessageEmits>();

const showMessage = ref<boolean>(true);

const messageIcon = computed<WangsIcons>(() => {
  if (props.loading) return 'spinner';

  switch (props.severity) {
    case 'success':
      return 'checkbox-circle-line';
    case 'danger':
      return 'close-circle-line';
    default:
      return 'information-line';
  }
});
</script>

<template>
  <div
    v-if="showMessage"
    v-bind="$preset.inlinemessage.root({ props })"
    aria-atomic="true"
    aria-live="assertive"
    role="alert"
  >
    <div class="flex items-center gap-3 w-full">
      <Icon
        v-if="showIcon"
        v-bind="$preset.inlinemessage.icon({ props })"
        :icon="messageIcon"
        :severity="props.severity"
      />

      <div
        class="flex items-center w-full justify-between"
        data-wv-section="content"
      >
        <section
          :class="[
            'flex flex-col font-medium',
            ...toArrayClass(props.messageWrapperClass),
          ]"
          data-wv-section="messagewrapper"
        >
          <span v-if="title" class="font-bold" data-wv-section="title">
            {{ props.title }}
          </span>

          <span data-wv-section="message">
            {{ props.loading ? `${props.message}...` : props.message }}
          </span>
        </section>

        <Button
          v-if="removable"
          :severity="props.severity"
          @click="
            () => {
              $emit('close');
              showMessage = false;
            }
          "
          class="[&_i]:w-6 [&_i]:h-6"
          data-wv-section="close"
          icon="close-line"
          rounded
          text
        />
      </div>
    </div>

    <div
      v-if="props.lists"
      :class="[
        'flex flex-col gap-3 overflow-auto max-h-[71px]',
        ...toArrayClass(props.listClass),
      ]"
      data-wv-section="list"
    >
      <div
        :key="index"
        v-for="(item, index) in props.lists"
        :class="[
          'flex items-center gap-2',
          ...toArrayClass(props.itemWrapperClass),
        ]"
        data-wv-section="item"
      >
        <Icon
          :severity="props.severity"
          class="w-4 h-4 !cursor-auto"
          icon="arrow-right-fill"
        />

        <span :class="[...toArrayClass(props.itemClass)]">{{ item }}</span>
      </div>
    </div>
  </div>
</template>
